/**
 * JobsContainer
 *
 * 책임:
 * - useJobs 훅으로 Job 목록 페칭
 * - 필터 옵션(모듈/상태/트리거) 구성
 * - 로딩/에러 상태 표시
 * - JobsScreen에 data + onOpenJob 콜백 전달
 *
 * 참고: JobsScreen은 필터 상태를 자체 관리(클라이언트 측)하므로,
 *       현재는 전체 Job을 페칭해 전달한다. 서버 측 필터 연결은 후속 작업.
 *
 * AdminApp에서 screen === 'jobs' 일 때 렌더링
 */

import { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useJobs } from '../hooks/useJobs';
import { useStageMatrix } from '../hooks/useStageMatrix';
import * as endpoints from '../api/endpoints';
import { JobsScreen } from '../screens/Jobs/JobsScreen';
import { LoadingView } from '../../design-system/molecules/LoadingView/LoadingView';
import { useMinimumLoading } from '../hooks/useMinimumLoading';
import type { JobFilters } from '../organisms/JobFilterBar/JobFilterBar';

// 뷰별 페이지 크기.
// 스테이지 뷰는 Job마다 /stages를 호출하는 매트릭스라 페이지당 건수를 작게(15) 둔다.
// 이 값이 곧 스테이지 매트릭스가 커버하는 건수이기도 하다(페이지=매트릭스 1:1).
const STAGE_PAGE_SIZE = 15;
const TABLE_PAGE_SIZE = 50;

export interface JobsContainerProps {
  // Job 상세 화면으로 이동 (AdminApp이 네비게이션 처리)
  onOpenJob: (id: number) => void;
  // 진입 시 자동 적용할 모듈 필터 (대시보드 "이력" 클릭 시)
  initialModule?: string;
}

// 상태/트리거 필터 옵션 (고정 목록)
const STATUS_OPTIONS = ['QUEUED', 'RUNNING', 'SUCCESS', 'FAILED'];
const TRIGGER_OPTIONS = ['manual', 'cron'];

export function JobsContainer({ onOpenJob, initialModule }: JobsContainerProps) {
  const { token } = useAuth();
  // 뷰 전환 상태를 여기서 관리 — 뷰별 페이지 크기를 useJobs에 전달해야 하므로 화면에서 끌어올렸다.
  // false=스테이지 뷰, true=테이블 뷰.
  const [tableView, setTableView] = useState(true);
  const pageSize = tableView ? TABLE_PAGE_SIZE : STAGE_PAGE_SIZE;

  // useJobs: Job 목록 페칭. 필터를 여기서 관리해 변경 시 서버 재조회가 일어나도록 연결.
  // initialModule이 있으면(대시보드 "이력" 클릭) 모듈 필터를 기본값으로 적용.
  // pageSize 변경(뷰 전환) 시 useJobs가 페이지를 0으로 리셋한다.
  const { data, loading, error, filters, setFilters, page, setPage } = useJobs(
    token,
    initialModule ? { module: initialModule } : undefined,
    pageSize
  );

  // 스테이지 매트릭스: 현재 페이지 전체(스테이지 뷰 페이지 크기 = 15)에 대해 /jobs/{id}/stages 병렬 조회.
  // early return보다 위에서 호출해야 hooks 규칙 위반이 없음(로딩 중엔 jobs=[]).
  const jobs = data?.jobs ?? [];
  const {
    columns: stageColumns,
    runs: stageRuns,
    loading: stageLoading,
  } = useStageMatrix(token, jobs.slice(0, STAGE_PAGE_SIZE));

  // 모듈 드롭다운 옵션: Job 목록(필터되면 붕괴)이 아니라 모듈 목록에서 독립적으로 조회.
  const [moduleOptions, setModuleOptions] = useState<string[]>([]);
  useEffect(() => {
    if (!token) return;
    endpoints
      .fetchModules()
      .then((r) => setModuleOptions(r.modules.map((m) => m.name)))
      .catch(() => setModuleOptions([]));
  }, [token]);

  // JobFilterBar가 기대하는 all-string 형태로 매핑 (api JobFilters는 옵셔널 필드)
  const filterValues: JobFilters = {
    module: filters.module ?? '',
    status: filters.status ?? '',
    trigger: filters.trigger ?? '',
    date: filters.date ?? '',
  };

  // 로딩 상태 — 최초 진입(데이터 없음)에만 전체 폴백.
  // 페이지 이동 시엔 이전 데이터를 유지해 페이지네이션 컨트롤이 사라지지 않게 한다.
  // 실제 로딩이 0.7초보다 빨리 끝나도 최소 0.7초는 로딩 화면을 유지.
  const showLoading = useMinimumLoading(loading && !data);
  if (showLoading) {
    return <LoadingView message="수집 이력 로딩 중…" />;
  }

  // 에러 상태
  if (error) {
    return (
      <div
        style={{
          padding: '2rem',
          color: '#c00',
          backgroundColor: '#fee',
          borderRadius: '0.5rem',
          margin: '2rem',
        }}
      >
        <div>❌ {error}</div>
      </div>
    );
  }

  // JobsScreen 렌더링: 필터는 controlled로 전달 (변경 시 useJobs가 서버 재조회)
  return (
    <JobsScreen
      data={{
        jobs,
        moduleOptions,
        statusOptions: STATUS_OPTIONS,
        triggerOptions: TRIGGER_OPTIONS,
      }}
      onOpenJob={onOpenJob}
      filters={filterValues}
      onFilterChange={(patch) => setFilters({ ...filters, ...patch })}
      tableView={tableView}
      onToggleView={setTableView}
      total={data?.total ?? 0}
      page={page}
      pageSize={pageSize}
      onPrev={() => setPage(Math.max(0, page - 1))}
      onNext={() => {
        if ((page + 1) * pageSize < (data?.total ?? 0)) setPage(page + 1);
      }}
      stageColumns={stageColumns}
      stageRuns={stageRuns}
      stageLoading={stageLoading}
    />
  );
}
